/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useRef, useMemo, useLayoutEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { withTheme } from 'styled-components';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import Map from '@mui/icons-material/Map';
import StyledProps from '../theme/AppThemeInterface';
import styled from 'styled-components';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import { selectMapSettingsPanelShown } from '../../../store';
import { Tooltip } from '@mui/material';

interface SettingsMapControlProps extends StyledProps {
  map: google.maps.Map;
  isFullScreen: boolean;
}

export function SettingsMapControl({ map, isFullScreen, theme }: SettingsMapControlProps) {
  const dispatch = useDispatch();
  const mapSettingsIcon = useRef<HTMLElement>();
  const mapActionsContext = useContext(MapActionsContext);
  const mapSettingsPanelShown = useSelectorWithMapId(selectMapSettingsPanelShown);
  const { toggleMapSettingsPanel } = mapActionsContext.mapActions;

  useLayoutEffect(() => {
    if (mapSettingsIcon.current && mapSettingsIcon.current.classList) {
      if (mapSettingsPanelShown) {
        mapSettingsIcon.current.classList.add('map_settings_icon--active');
      } else {
        mapSettingsIcon.current.classList.remove('map_settings_icon--active');
      }
    }
  }, [mapSettingsPanelShown]);

  const items = useMemo(
    () => [
      {
        render: () => (
          <Tooltip
            arrow
            title="Map Settings"
            placement="left-start"
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: theme.tooltipBackgroundColor },
              },
              arrow: {
                sx: { color: theme.tooltipBackgroundColor },
              },
            }}
          >
            <StyledMapIcon id="map_settings_panel_btn" ref={mapSettingsIcon} />
          </Tooltip>
        ),
        buttonTextColor: theme.secondaryButtonTextColor,
        id: 'map_settings_panel_btn_container',
        onClick: () => {
          dispatch(toggleMapSettingsPanel());
        },
      },
    ],
    [dispatch, theme, mapSettingsIcon, toggleMapSettingsPanel],
  );

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={32}
      hidden={isFullScreen}
      index={MapControlOrderIndex.MAP_SETTTINGS_CONTROL}
    />
  );
}

export default withTheme(SettingsMapControl);

const StyledMapIcon = styled(Map)`
  width: 20px !important;
`;
